import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { ApiError } from '../../api/errors';
import type {
  DocumentView,
  Location,
  MetadataField,
  OcrTextResponse,
  OverviewField,
  Scanner,
} from '../../api/types';
import { useSession } from '../../auth/session';
import {
  Button,
  Combobox,
  ConfidenceMeter,
  ErrorState,
  Skeleton,
  StatusBadge,
  Tabs,
  useToast,
} from '../../components';
import { useOnClickOutside, useOnEscape } from '../../lib/hooks';

import { useDocumentsApi } from './client';
import './documents.css';
import {
  buildHistory,
  categoryOf,
  confidenceOf,
  dateTimeLabel,
  pagesLabel,
  rationaleFor,
  sizeLabel,
  sourceLabel,
  toDocStatus,
} from './format';
import { PreviewBody } from './PreviewBody';
import { clampZoom, hasStoredOriginal, ZOOM_MAX, ZOOM_MIN, ZOOM_STEP } from './preview';
import { useDocument, type DocumentData } from './useDocument';
import { useDocumentPreview } from './useDocumentPreview';

export function DocumentDetailScreen(): React.JSX.Element {
  const { documentId = '' } = useParams<{ documentId: string }>();
  const location = useLocation();
  const state = useDocument(documentId);

  const backTo = `/documents${location.search}`;

  if (state.status === 'loading') return <DetailLoading backTo={backTo} />;

  if (state.status === 'not-found') {
    return (
      <section className="ds-docd" aria-label="Document detail">
        <DetailHeaderLite backTo={backTo} />
        <ErrorState
          title="This document isn't available"
          body="It may have been deleted, expired under your retention policy, or never existed in this tenant."
          actions={
            <Link className="ds-docd__crumb" to={backTo}>
              ← Back to Documents
            </Link>
          }
        />
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className="ds-docd" aria-label="Document detail">
        <DetailHeaderLite backTo={backTo} />
        <ErrorState
          title="We couldn't load this document"
          body="The documents service didn't respond. This only affects the detail view."
          traceId={state.traceId ?? undefined}
          actions={
            <Button variant="primary" onClick={state.reload}>
              Retry
            </Button>
          }
        />
      </section>
    );
  }

  if (state.data.document.status === 'DELETED') {
    return (
      <section className="ds-docd" aria-label="Document detail">
        <DetailHeaderLite backTo={backTo} filename={state.data.document.filename} />
        <ErrorState
          title="This document has been deleted"
          body="The original and its extracted text were removed under the tenant retention policy. Its audit trail is retained."
          actions={
            <Link className="ds-docd__crumb" to={backTo}>
              ← Back to Documents
            </Link>
          }
        />
      </section>
    );
  }

  return <DetailReady data={state.data} documentId={documentId} reload={state.reload} backTo={backTo} />;
}

function locationNameOf(doc: DocumentView, locations: Location[]): string | null {
  const id = doc.capture_metadata.location_id;
  if (id == null) return null;
  return locations.find((l) => l.id === id)?.name ?? id;
}

function scannerLabelOf(doc: DocumentView, scanners: Scanner[]): string | null {
  const id = doc.capture_metadata.scanner_id;
  if (id == null) return null;
  const scanner = scanners.find((s) => s.id === id);
  if (scanner === undefined) return id;
  return scanner.model != null ? `${scanner.device_id} (${scanner.model})` : scanner.device_id;
}

function DetailReady({
  data,
  documentId,
  reload,
  backTo,
}: {
  data: DocumentData;
  documentId: string;
  reload: () => void;
  backTo: string;
}): React.JSX.Element {
  const { document: doc, categories, visibility } = data;
  const docsApi = useDocumentsApi();
  const { toast } = useToast();
  const { hasPermission } = useSession();
  const canResolve = hasPermission('review:resolve');

  // Partner-configured (tasks/14): which Document Detail tabs/fields this
  // tenant's screen should hide. Empty by default — nothing hidden.
  const hiddenTabs = new Set(visibility.hidden_tabs);
  const hiddenOverviewFields = new Set(visibility.hidden_overview_fields);
  const hiddenMetadataFields = new Set(visibility.hidden_metadata_fields);

  const [tab, setTab] = useState('overview');
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(menuRef, menuOpen, () => { setMenuOpen(false); });
  useOnEscape(menuOpen, () => { setMenuOpen(false); });

  const badge = toDocStatus(doc.status);
  const locationName = locationNameOf(doc, data.locations);
  const scannerLabel = scannerLabelOf(doc, data.scanners);

  const correct = useCallback(
    (categoryId: string) => {
      setMenuOpen(false);
      setBusy(true);
      void (async () => {
        try {
          await docsApi.correct(documentId, categoryId);
          toast('Category updated — correction logged to audit trail');
          reload();
        } catch {
          toast('Could not update the category — please retry.', { tone: 'error' });
        } finally {
          setBusy(false);
        }
      })();
    },
    [docsApi, documentId, toast, reload],
  );

  const reclassify = useCallback(() => {
    setBusy(true);
    void (async () => {
      try {
        await docsApi.reclassify(documentId);
        toast('Queued for re-classification — published document.reclassify to q.reclassify');
        // The backend resets the document to `OCR_DONE` before publishing, so
        // reloading now picks that up and arms this screen's existing
        // still-mid-pipeline polling (`useDocument`'s `isInFlight`) — the status
        // badge advances on its own once the worker finishes, same as a fresh
        // upload, instead of requiring a manual page reload to see it land.
        reload();
      } catch (error) {
        // The backend rejects a re-classify with no extracted text (422) or on a
        // document already in a terminal state — deleted/failed (409).
        const message =
          error instanceof ApiError && error.status === 422
            ? "Can't re-classify yet — this document has no extracted OCR text."
            : error instanceof ApiError && error.status === 409
              ? "Can't re-classify — this document is in a terminal state."
              : 'Could not queue re-classification — please retry.';
        toast(message, { tone: 'error' });
      } finally {
        setBusy(false);
      }
    })();
  }, [docsApi, documentId, toast, reload]);

  /**
   * Save the stored original. The bytes have to be fetched rather than linked to
   * — the endpoint needs the bearer token, which the browser attaches to neither
   * an `<a href>` navigation nor a subresource load — so this pulls the blob and
   * hands it to a synthetic anchor. Typically it is already in the HTTP cache
   * from the preview, which shares the URL.
   */
  const download = useCallback(() => {
    setBusy(true);
    void (async () => {
      try {
        const blob = await docsApi.original(documentId);
        const url = URL.createObjectURL(blob);
        const anchor = window.document.createElement('a');
        anchor.href = url;
        anchor.download = doc.filename;
        window.document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        // Revoked on the next tick, not immediately: some browsers read the blob
        // asynchronously after the click and a same-tick revoke cancels the save.
        window.setTimeout(() => { URL.revokeObjectURL(url); }, 0);
      } catch (error) {
        const message =
          error instanceof ApiError && error.status === 404
            ? 'The stored original is no longer available for this document.'
            : 'Could not download the original — please retry.';
        toast(message, { tone: 'error' });
      } finally {
        setBusy(false);
      }
    })();
  }, [docsApi, documentId, doc.filename, toast]);

  const tabs = [
    hiddenTabs.has('overview')
      ? null
      : {
          id: 'overview',
          label: 'Overview',
          content: (
            <OverviewTab
              doc={doc}
              locationName={locationName}
              scannerLabel={scannerLabel}
              hidden={hiddenOverviewFields}
            />
          ),
        },
    hiddenTabs.has('ocr_text')
      ? null
      : {
          id: 'ocr',
          label: 'OCR text',
          content: (
            <OcrTab doc={doc} documentId={documentId} onCopied={() => { toast('OCR text copied'); }} />
          ),
        },
    hiddenTabs.has('metadata')
      ? null
      : {
          id: 'metadata',
          label: 'Metadata',
          content: (
            <MetadataTab
              doc={doc}
              locationName={locationName}
              scannerLabel={scannerLabel}
              hidden={hiddenMetadataFields}
            />
          ),
        },
    hiddenTabs.has('history')
      ? null
      : {
          id: 'history',
          label: 'History',
          content: <HistoryTab doc={doc} locationName={locationName} scannerLabel={scannerLabel} />,
        },
  ].filter((entry): entry is NonNullable<typeof entry> => entry !== null);

  return (
    <section className="ds-docd" aria-label="Document detail">
      <div className="ds-docd__head">
        <Link className="ds-docd__crumb" to={backTo}>
          ← Documents
        </Link>
        <span className="ds-docd__sep" aria-hidden="true">
          /
        </span>
        <span className="ds-docd__fn">{doc.filename}</span>
        <StatusBadge status={badge} className="ds-docd__headstatus" />
        <div className="ds-docd__actions" ref={menuRef}>
          {canResolve ? (
            <>
              <button
                type="button"
                className="ds-docd__btn"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                disabled={busy}
                onClick={() => { setMenuOpen((o) => !o); }}
              >
                Change category ▾
              </button>
              {menuOpen ? (
                <div className="ds-docd__catmenu" role="menu" aria-label="Change category">
                  <Combobox
                    label="Change category"
                    hideLabel
                    placeholder="Search categories…"
                    options={categories.map((c) => ({ value: c.id, label: c.name }))}
                    value={doc.classification?.category_id ?? null}
                    onChange={correct}
                  />
                  <p className="ds-docd__catmenu-note">Corrections are audited &amp; used as training feedback</p>
                </div>
              ) : null}
              <button type="button" className="ds-docd__btn" disabled={busy} onClick={reclassify}>
                Re-run classification
              </button>
            </>
          ) : null}
          <Button variant="primary" onClick={download} disabled={busy}>
            Download
          </Button>
        </div>
      </div>

      <div className="ds-docd__body">
        <PreviewPane doc={doc} documentId={documentId} onDownload={download} />
        <div className="ds-docd__panel">
          <Tabs tabs={tabs} value={tab} onChange={setTab} label="Document detail sections" />
        </div>
      </div>
    </section>
  );
}

// --------------------------------------------------------------------- preview

/**
 * The stored original, rendered by whichever means the browser actually has.
 *
 * A PDF goes into an `<iframe>` and is drawn by the browser's own viewer — which
 * brings its own paging, zoom, search and print, so this pane deliberately shows
 * no page/zoom chrome of its own for PDFs rather than a second set of controls
 * that could not drive the first. Images get a real zoom control, because there
 * is nothing else to provide one. Plain text is shown as text. Anything a
 * browser cannot draw — TIFF above all — says so and points at the download.
 */
function PreviewPane({
  doc,
  documentId,
  onDownload,
}: {
  doc: DocumentView;
  documentId: string;
  onDownload: () => void;
}): React.JSX.Element {
  const previewable = hasStoredOriginal(doc.status);
  const state = useDocumentPreview(documentId, doc.mime, previewable);
  const [zoom, setZoom] = useState(1);

  const pageCount = doc.page_count != null && doc.page_count > 0 ? doc.page_count : null;
  const facts = [
    doc.mime,
    pageCount != null ? `${String(pageCount)} page${pageCount === 1 ? '' : 's'}` : null,
    sizeLabel(doc.size_bytes),
  ].filter((part): part is string => part !== null);

  return (
    <div className="ds-docd__preview">
      <div className="ds-docd__preview-bar">
        <span className="ds-docd__preview-facts">{facts.join(' · ')}</span>
        {state.status === 'ready' && state.kind === 'image' ? (
          <ZoomControl zoom={zoom} setZoom={setZoom} />
        ) : null}
      </div>
      <div className="ds-docd__stage">
        <PreviewBody
          state={state}
          doc={doc}
          zoom={zoom}
          previewable={previewable}
          onDownload={onDownload}
        />
      </div>
    </div>
  );
}

function ZoomControl({
  zoom,
  setZoom,
}: {
  zoom: number;
  setZoom: (z: number) => void;
}): React.JSX.Element {
  const zoomPct = `${String(Math.round(zoom * 100))}%`;
  return (
    <div className="ds-docd__zoom">
      <button
        type="button"
        className="ds-docd__zoombtn"
        aria-label="Zoom out"
        disabled={zoom <= ZOOM_MIN + 0.001}
        onClick={() => { setZoom(clampZoom(zoom - ZOOM_STEP)); }}
      >
        −
      </button>
      <span className="ds-docd__zoomval" aria-label={`Zoom ${zoomPct}`}>
        {zoomPct}
      </span>
      <button
        type="button"
        className="ds-docd__zoombtn"
        aria-label="Zoom in"
        disabled={zoom >= ZOOM_MAX - 0.001}
        onClick={() => { setZoom(clampZoom(zoom + ZOOM_STEP)); }}
      >
        +
      </button>
    </div>
  );
}

// -------------------------------------------------------------------- overview

function OverviewTab({
  doc,
  locationName,
  scannerLabel,
  hidden,
}: {
  doc: DocumentView;
  locationName: string | null;
  scannerLabel: string | null;
  hidden: ReadonlySet<OverviewField>;
}): React.JSX.Element {
  const cls = doc.classification;
  const name = categoryOf(doc);
  const conf = confidenceOf(doc);
  const confColour = conf != null && conf >= 0.75 ? 'var(--ok-ink)' : 'var(--warn)';
  const rationale = rationaleFor(doc.classification);
  const fileParts = [
    doc.page_count != null && doc.page_count > 0 ? `${String(doc.page_count)} pages` : null,
    sizeLabel(doc.size_bytes),
    doc.mime,
    `ingested ${dateTimeLabel(doc.ingested_at)}`,
  ].filter((p): p is string => p !== null);

  return (
    <div className="ds-docd__ov">
      <div className="ds-docd__clsbox">
        <div className="ds-docd__cls-row">
          <span className="ds-docd__cls-name">{name === '—' ? 'Not classified' : name}</span>
          <span className="ds-docd__cls-eyebrow">classification</span>
          {conf != null ? (
            <span className="ds-docd__cls-conf" style={{ color: confColour }}>
              {conf.toFixed(2)}
            </span>
          ) : null}
        </div>
        <ConfidenceMeter value={conf} size={8} label="Classification confidence" />
        <div className="ds-docd__scale">
          <span>confidence</span>
          <span>threshold 0.75</span>
        </div>
        {hidden.has('classification_rationale') ? null : (
          <p className="ds-docd__why">
            <b>Why: </b>
            {rationale}
          </p>
        )}
        {cls != null && !hidden.has('classification_versions') ? (
          <div className="ds-docd__chips">
            {cls.model_version != null ? (
              <span className="ds-docd__chip" title="Azure OpenAI deployment used">
                {cls.model_version}
              </span>
            ) : null}
            {cls.prompt_version != null ? (
              <span className="ds-docd__chip" title="Classification prompt version">
                prompt {cls.prompt_version}
              </span>
            ) : null}
            {cls.taxonomy_version != null ? (
              <span className="ds-docd__chip" title="Tenant taxonomy version at classification time">
                taxonomy v{cls.taxonomy_version}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {doc.summary != null && doc.summary.trim() !== '' && !hidden.has('ai_summary') ? (
        <div>
          <p className="ds-docd__eyebrow">AI SUMMARY</p>
          <p className="ds-docd__summary">{doc.summary}</p>
        </div>
      ) : null}

      <div>
        <p className="ds-docd__eyebrow">KEY METADATA</p>
        <div className="ds-docd__kv">
          <KvRow label="Location" value={locationName ?? '—'} />
          <KvRow label="Scanner" value={scannerLabel ?? '—'} mono />
          <KvRow label="Source" value={sourceLabel(doc.capture_metadata.source.channel)} />
          <KvRow label="Ingested" value={dateTimeLabel(doc.ingested_at)} />
        </div>
        <p className="ds-docd__fileline">{fileParts.join(' · ')}</p>
      </div>
    </div>
  );
}

function KvRow({ label, value, mono }: { label: string; value: string; mono?: boolean }): React.JSX.Element {
  return (
    <div className="ds-docd__kv-row">
      <span className="ds-docd__kv-key">{label}</span>
      <span className="ds-docd__kv-val" style={mono ? { fontFamily: "var(--mono, 'Geist Mono'), monospace" } : undefined}>
        {value}
      </span>
    </div>
  );
}

// ------------------------------------------------------------------------- OCR

/** A plain OCR note (no fetch): the pre-OCR / failed doc states. */
function OcrNote({ text }: { text: string }): React.JSX.Element {
  return (
    <div className="ds-docd__ocr">
      <pre className="ds-docd__ocr-block">{text}</pre>
    </div>
  );
}

type OcrState =
  | { status: 'loading' }
  | { status: 'ready'; data: OcrTextResponse }
  | { status: 'empty' }
  | { status: 'error' };

function isAbortErr(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * The OCR tab. For a document past OCR it fetches the real stored extracted text
 * (`GET /v1/documents/{id}/ocr-text`) and renders it with its language and OCR
 * confidence; a 404 means nothing is stored yet. FAILED / RECEIVED documents
 * have no text to fetch and show the matching note instead.
 */
function OcrTab({
  doc,
  documentId,
  onCopied,
}: {
  doc: DocumentView;
  documentId: string;
  onCopied: () => void;
}): React.JSX.Element {
  if (doc.status === 'FAILED') {
    return <OcrNote text="OCR unavailable — processing did not complete." />;
  }
  if (doc.status === 'RECEIVED') {
    return <OcrNote text="OCR is still processing — extracted text will appear here once complete." />;
  }
  return <OcrFetched doc={doc} documentId={documentId} onCopied={onCopied} />;
}

function OcrFetched({
  doc,
  documentId,
  onCopied,
}: {
  doc: DocumentView;
  documentId: string;
  onCopied: () => void;
}): React.JSX.Element {
  const docsApi = useDocumentsApi();
  const [state, setState] = useState<OcrState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    setState({ status: 'loading' });
    void (async () => {
      try {
        const data = await docsApi.ocrText(documentId, signal);
        if (signal.aborted) return;
        setState({ status: 'ready', data });
      } catch (error) {
        if (signal.aborted || isAbortErr(error)) return;
        if (error instanceof ApiError && error.status === 404) {
          setState({ status: 'empty' });
          return;
        }
        setState({ status: 'error' });
      }
    })();
    return () => {
      controller.abort();
    };
  }, [docsApi, documentId, nonce]);

  if (state.status === 'loading') {
    return (
      <div className="ds-docd__ocr" aria-busy="true">
        <Skeleton width="100%" height={200} radius={8} />
      </div>
    );
  }
  if (state.status === 'empty') {
    return <OcrNote text="No OCR text is available for this document." />;
  }
  if (state.status === 'error') {
    return (
      <div className="ds-docd__ocr">
        <ErrorState
          title="We couldn't load the OCR text"
          body="The extracted text didn't load. This only affects the OCR tab."
          actions={
            <Button variant="secondary" onClick={reload}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  const { text, language, ocr_confidence: ocrConfidence, page_count: pageCount } = state.data;
  const model = doc.ocr_model_version ?? 'Azure Document Intelligence';
  const lang = language ?? doc.language ?? 'unknown';
  const copy = (): void => {
    const nav = navigator as { clipboard?: Clipboard };
    if (nav.clipboard) void nav.clipboard.writeText(text).catch(() => undefined);
    onCopied();
  };
  const footParts = [
    `OCR by ${model}`,
    `language ${lang}`,
    ocrConfidence != null ? `confidence ${ocrConfidence.toFixed(2)}` : null,
    pageCount != null ? `${String(pageCount)} page${pageCount === 1 ? '' : 's'}` : null,
  ].filter((p): p is string => p !== null);

  return (
    <div className="ds-docd__ocr">
      <div className="ds-docd__ocr-head">
        <p className="ds-docd__eyebrow" style={{ margin: 0 }}>
          EXTRACTED TEXT
        </p>
        <button type="button" className="ds-docd__copy" onClick={copy}>
          Copy
        </button>
      </div>
      <pre className="ds-docd__ocr-block">{text}</pre>
      <p className="ds-docd__ocr-foot">{footParts.join(' · ')}</p>
    </div>
  );
}

// -------------------------------------------------------------------- metadata

function MetadataTab({
  doc,
  locationName,
  scannerLabel,
  hidden,
}: {
  doc: DocumentView;
  locationName: string | null;
  scannerLabel: string | null;
  hidden: ReadonlySet<MetadataField>;
}): React.JSX.Element {
  const candidates: [MetadataField, string, string][] = [
    ['filename', 'Filename', doc.filename],
    ['status', 'Status', toDocStatus(doc.status)],
    ['location', 'Location', locationName ?? '—'],
    ['scanner', 'Scanner', scannerLabel ?? '—'],
    ['source', 'Source channel', sourceLabel(doc.capture_metadata.source.channel)],
    ['operator_ref', 'Operator ref', doc.capture_metadata.operator_user_ref ?? '—'],
    ['scanned_at', 'Scanned at', dateTimeLabel(doc.capture_metadata.scanned_at)],
    ['ingested_at', 'Ingested at', dateTimeLabel(doc.ingested_at)],
    ['pages', 'Pages', pagesLabel(doc.page_count)],
    ['size', 'Size', sizeLabel(doc.size_bytes)],
    ['language', 'Language', doc.language ?? '—'],
    ['ocr_confidence', 'OCR confidence', doc.ocr_confidence != null ? doc.ocr_confidence.toFixed(2) : '—'],
    ['content_hash', 'Content hash', doc.content_hash ?? '—'],
  ];
  const rows: [string, string][] = candidates
    .filter(([field]) => !hidden.has(field))
    .map(([, label, value]) => [label, value]);
  const custom = hidden.has('custom_metadata') ? [] : Object.entries(doc.custom_metadata ?? {});
  for (const [key, value] of custom) {
    rows.push([key, formatCustom(value)]);
  }
  return (
    <div className="ds-docd__meta">
      {rows.map(([key, value]) => (
        <div key={key} className="ds-docd__meta-row">
          <span className="ds-docd__meta-key">{key}</span>
          <span className="ds-docd__meta-val" title={value}>
            {value}
          </span>
        </div>
      ))}
      <p className="ds-docd__meta-foot">
        Includes capture metadata and tenant custom fields
        {custom.length > 0 ? ` (${custom.map(([k]) => k).join(', ')})` : ''}
      </p>
    </div>
  );
}

function formatCustom(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

// -------------------------------------------------------------------- history

function HistoryTab({
  doc,
  locationName,
  scannerLabel,
}: {
  doc: DocumentView;
  locationName: string | null;
  scannerLabel: string | null;
}): React.JSX.Element {
  const entries = buildHistory(doc, { locationName, scannerLabel });
  return (
    <div className="ds-docd__hist">
      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {entries.map((entry, index) => (
          <li key={`${entry.text}-${String(index)}`} className="ds-docd__hist-entry">
            <div className="ds-docd__hist-rail" aria-hidden="true">
              <span className="ds-docd__hist-dot" style={{ background: entry.dot }} />
              {index < entries.length - 1 ? <span className="ds-docd__hist-line" /> : null}
            </div>
            <div className="ds-docd__hist-content">
              {entry.time !== '' ? <div className="ds-docd__hist-time">{entry.time}</div> : null}
              <div className="ds-docd__hist-text">{entry.text}</div>
            </div>
          </li>
        ))}
      </ol>
      <p className="ds-docd__hist-foot">Every change writes an audit entry (GDPR Art. 30)</p>
    </div>
  );
}

// ---------------------------------------------------------------------- chrome

function DetailHeaderLite({ backTo, filename }: { backTo: string; filename?: string }): React.JSX.Element {
  return (
    <div className="ds-docd__head">
      <Link className="ds-docd__crumb" to={backTo}>
        ← Documents
      </Link>
      {filename !== undefined ? (
        <>
          <span className="ds-docd__sep" aria-hidden="true">
            /
          </span>
          <span className="ds-docd__fn">{filename}</span>
        </>
      ) : null}
    </div>
  );
}

function DetailLoading({ backTo }: { backTo: string }): React.JSX.Element {
  return (
    <section className="ds-docd" aria-label="Document detail" aria-busy="true">
      <DetailHeaderLite backTo={backTo} />
      <div className="ds-docd__body">
        <div className="ds-docd__preview">
          <div className="ds-docd__preview-bar">
            <Skeleton width={120} height={16} radius={5} />
          </div>
          <div className="ds-docd__stage">
            <Skeleton width={340} height={476} radius={4} />
          </div>
        </div>
        <div className="ds-docd__panel" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Skeleton width="60%" height={20} radius={5} />
          <Skeleton width="100%" height={80} radius={8} />
          <Skeleton width="90%" height={16} radius={5} />
          <Skeleton width="80%" height={16} radius={5} />
        </div>
      </div>
    </section>
  );
}
